/**
 * Module: api/creator
 * Role: Typed wrappers for buyer creator-role HTTP endpoints (FTR-1106).
 *       Mirrors `src/api/v2/buyer/_creator_schemas.py` shapes manually (L-240:
 *       inline TS/Zod schemas, never import from api/types.ts — keeps each
 *       miniapp module self-describing and survives backend refactors).
 *
 *       Each response is parsed through a Zod schema before returning so we
 *       fail loud (with a precise issue path) when backend drifts vs the
 *       Pydantic source-of-truth, instead of crashing in a far-away component.
 *
 * Uses: ./client:apiFetch, zod
 * Used by:
 *   - screens/creator/* (wizard screens)
 *   - screens/creator/ProfileEditScreen.tsx (профиль креатора: площадки + аудитория, FTR-1458)
 *   - screens/ProfileScreen.tsx (creator status row)
 *   - screens/HomeScreen/* (promo CTA)
 *   - screens/TasksScreen.tsx (banner CTA)
 *
 * NOTE: TypeScript types below are a manual mirror of the Pydantic source-of-truth
 * at src/api/v2/buyer/_creator_schemas.py. Keep in sync when the Python models change.
 */

import { z } from 'zod';
import { apiFetch } from './client';

// ---------------------------------------------------------------------------
// Zod schemas — manual mirror of src/api/v2/buyer/_creator_schemas.py
// ---------------------------------------------------------------------------

const CreatorApplicationStatusEnum = z.enum([
  'pending',
  'pending_scraping',
  'approved',
  'rejected',
  'needs_info',
  'banned',
]);

const VerificationMethodEnum = z.enum([
  'vision_ocr',
  'vk_api',
  'vk_api+vision',
  'manual',
]);

const CreatorApplicationSchema = z.object({
  id: z.number().int(),
  buyer_id: z.string(), // UUID — backend serializes as string
  status: CreatorApplicationStatusEnum,
  portfolio_url: z.string(),
  sample_urls: z.array(z.string()).nullable(),
  bio: z.string().nullable(),
  application_data: z.record(z.string(), z.unknown()).nullable(),
  rejection_reason: z.string().nullable(),
  reviewed_by: z.string().nullable(),
  reviewed_at: z.string().nullable(), // ISO-8601
  created_at: z.string(),
  updated_at: z.string(),
  selfie_url: z.string().nullable(),
  vk_url: z.string().nullable(),
  tiktok_url: z.string().nullable(),
  parsed_follower_count_vk: z.number().int().nullable(),
  parsed_follower_count_tiktok: z.number().int().nullable(),
  verification_confidence: z.number().nullable(),
  verification_method: VerificationMethodEnum.nullable(),
  selfie_verified_at: z.string().nullable(),
  social_api_verified_at: z.string().nullable(),
  avg_views_30d_vk: z.number().int().nullable(),
  avg_views_90d_vk: z.number().int().nullable(),
});

const CreatorStateSchema = z.object({
  account_kind: z.enum(['standard', 'creator']),
  application: CreatorApplicationSchema.nullable(),
  can_apply_at: z.string().nullable(),
  // FTR-1106 Task 3: mirrors _creator_schemas.py:CreatorState.creator_profile_complete.
  // Default false for backward compat (old backend versions won't return this field).
  creator_profile_complete: z.boolean().default(false),
});

const CreatorVerifyStatusViewSchema = z.object({
  status: z.string(), // 'no_application' | CreatorApplicationStatus
  application_id: z.number().int().nullable(),
  portfolio_url: z.string().nullable(),
  rejection_reason: z.string().nullable(),
  selfie_url: z.string().nullable(),
  vk_url: z.string().nullable(),
  tiktok_url: z.string().nullable(),
  parsed_follower_count_vk: z.number().int().nullable(),
  parsed_follower_count_tiktok: z.number().int().nullable(),
  verification_confidence: z.number().nullable(),
  verification_method: z.string().nullable(),
  selfie_verified_at: z.string().nullable(),
  social_api_verified_at: z.string().nullable(),
  avg_views_30d_vk: z.number().int().nullable(),
  avg_views_90d_vk: z.number().int().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

const CreatorOptionsLimitsSchema = z.object({
  max_formats: z.number().int(),
  max_cities: z.number().int(),
  city_max_chars: z.number().int(),
  // FTR-1458: backend field is new — `.default(5)` keeps old backend responses
  // (and the existing status-screen.test.tsx mock) parsing without this key.
  max_socials: z.number().int().default(5),
});

const CreatorOptionsResponseSchema = z.object({
  categories: z.array(z.string()),
  formats: z.array(z.string()),
  genders: z.array(z.string()),
  age_ranges: z.array(z.string()),
  limits: CreatorOptionsLimitsSchema,
});

const CreatorVerifyResponseSchema = z.object({
  application_id: z.number().int(),
  // TECH-1456 / FTR-1452: 'needs_info' — тоже УСПЕХ подачи (HTTP 202), не ошибка.
  // Пока его тут не было, ответ бэкенда падал в catch CitiesStep и человек читал
  // «Не удалось отправить заявку. Попробуйте ещё раз.» — при созданной заявке.
  // SYMMETRIC LIMIT (ADR-022) — sync with
  //   src/api/v2/buyer/_creator_schemas.py:CreatorVerifyResponse.status
  status: z.enum(['pending_scraping', 'rejected', 'needs_info']),
  rejection_reason: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Inferred TS types (single source — `z.infer` from the schemas above)
// ---------------------------------------------------------------------------

export type CreatorApplicationStatus = z.infer<typeof CreatorApplicationStatusEnum>;
export type CreatorVerificationMethod = z.infer<typeof VerificationMethodEnum>;
export type CreatorApplication = z.infer<typeof CreatorApplicationSchema>;
export type CreatorState = z.infer<typeof CreatorStateSchema>;
export type CreatorVerifyStatusView = z.infer<typeof CreatorVerifyStatusViewSchema>;
export type CreatorOptionsLimits = z.infer<typeof CreatorOptionsLimitsSchema>;
export type CreatorOptionsResponse = z.infer<typeof CreatorOptionsResponseSchema>;
export type CreatorVerifyResponse = z.infer<typeof CreatorVerifyResponseSchema>;

/**
 * POST /api/v2/buyer/creator/verify_apply request body.
 * At least one of vk_url / tiktok_url is required (enforced server-side).
 *
 * Kept as a hand-written interface (not z.infer) because the request shape
 * has optional/conditional fields with cross-field validation handled by
 * React Hook Form + the per-step Zod form schema (Task 3).
 */
export interface CreatorVerifyRequest {
  selfie_base64: string;
  vk_url?: string;
  tiktok_url?: string;
  screenshot_base64?: string;
  profile_data?: Record<string, unknown>;
}

/**
 * Custom Error subclass for verify_apply rate-limit (HTTP 429).
 * Carries the parsed Retry-After header (seconds) so screens can render
 * "Повторите попытку через X сек." without re-parsing the response.
 */
export class CreatorVerifyRateLimitError extends Error {
  readonly status = 429;
  readonly retryAfter: number | null;

  constructor(retryAfter: number | null) {
    super(
      retryAfter !== null
        ? `creator/verify_apply rate-limited (retry in ${retryAfter}s)`
        : 'creator/verify_apply rate-limited',
    );
    this.name = 'CreatorVerifyRateLimitError';
    this.retryAfter = retryAfter;
  }
}

// ---------------------------------------------------------------------------
// API wrappers
// ---------------------------------------------------------------------------

/** GET /api/v2/buyer/creator/status — current account_kind + application snapshot. */
export async function getCreatorStatus(): Promise<CreatorState> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/status');
  return CreatorStateSchema.parse(raw);
}

/**
 * GET /api/v2/buyer/creator/options — wizard option lists + limits (SSOT).
 *
 * Use staleTime: Infinity in React Query — options are pure enum, never change at runtime.
 */
export async function getCreatorOptions(): Promise<CreatorOptionsResponse> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/options');
  return CreatorOptionsResponseSchema.parse(raw);
}

/**
 * POST /api/v2/buyer/creator/verify_apply — submit selfie + social URL for verification.
 *
 * On HTTP 429 (rate-limit, 1/24h), throws `CreatorVerifyRateLimitError` with
 * `retryAfter` (seconds, may be null if header missing). All other errors
 * bubble up as the generic Error & {status} from apiFetch.
 */
export async function submitVerifyApply(
  req: CreatorVerifyRequest,
): Promise<CreatorVerifyResponse> {
  try {
    const raw = await apiFetch<unknown>('/api/v2/buyer/creator/verify_apply', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return CreatorVerifyResponseSchema.parse(raw);
  } catch (err) {
    const e = err as Error & { status?: number; retryAfter?: number | null };
    if (e.status === 429) {
      throw new CreatorVerifyRateLimitError(e.retryAfter ?? null);
    }
    throw err;
  }
}

/** GET /api/v2/buyer/creator/verify_status — lightweight status poll. */
export async function getVerifyStatus(): Promise<CreatorVerifyStatusView> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/verify_status');
  return CreatorVerifyStatusViewSchema.parse(raw);
}

/**
 * Исход проверки владения профилем (FTR-1452).
 *
 * SYMMETRIC LIMIT (ADR-022) — набор значений синхронен с:
 *   - src/domains/buyer/services/creator_ownership.py:OwnershipOutcome
 *   - src/api/v2/buyer/_creator_schemas.py:CreatorOwnershipCheckResponse
 *   - supabase/migrations/20260820140000_ftr1452_ownership_proof.sql (CHECK)
 */
const OwnershipCheckResponseSchema = z.object({
  outcome: z.enum(['ok', 'not_subscribed', 'code_not_found', 'cannot_check']).nullable().optional(),
  method: z.enum(['vk_subscription', 'tiktok_bio_code']).nullable().optional(),
  message: z.string().nullable().optional(),
  retry_after_sec: z.number().nullable().optional(),
});

export type OwnershipCheckResponse = z.infer<typeof OwnershipCheckResponseSchema>;

/**
 * POST /api/v2/buyer/creator/ownership/recheck — кнопка «Проверить ещё раз».
 *
 * Тела у запроса нет намеренно: сервер берёт ссылки из заявки самого человека.
 * Передавать их с клиента значило бы дать способ подставить чужой профиль.
 */
export async function recheckOwnership(): Promise<OwnershipCheckResponse> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/ownership/recheck', {
    method: 'POST',
  });
  return OwnershipCheckResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// FTR-1458: creator profile screen — площадки (socials) CRUD.
// Mirrors src/api/v2/buyer/_creator_schemas.py: CreatorSocialView,
// CreatorProfileView, CreatorSocialUpsertRequest.
// ---------------------------------------------------------------------------

// SYMMETRIC LIMIT (ADR-022) — sync with:
//   src/domains/buyer/models_creator.py:SocialNetwork / SocialVerifiedSource
//   src/api/v2/buyer/_creator_schemas.py:CreatorSocialNetwork / CreatorSocialVerifiedSource
//   supabase/migrations/20260820150000_ftr1453_creator_socials.sql (CHECK)
// FTR-1458 Task 3+4: `network` is `z.string()`, not a client-side copy of the enum —
// the backend is the SSOT (CreatorProfileView.available_networks tells the FE which
// networks can still be added). Avoids a second copy that drifts AND collides with
// the ADR-023 platform-isolation gate (a literal `'vk'`/`'telegram'` string outside
// `src/platform/` fails `platform-isolation.arch.test.ts`).
const SocialVerifiedSourceEnum = z.enum(['api', 'self_reported', 'vision']);

const CreatorSocialSchema = z.object({
  network: z.string(),
  profile_url: z.string(),
  handle: z.string().nullable().optional(),
  follower_count: z.number().int().nullable().optional(),
  verified_source: SocialVerifiedSourceEnum,
  checked_at: z.string().nullable().optional(),
});

const CreatorProfileViewSchema = z.object({
  profile: z.record(z.string(), z.unknown()).nullable(),
  socials: z.array(CreatorSocialSchema),
  available_networks: z.array(z.string()).default([]),
});

export type SocialNetwork = string;
export type CreatorSocial = z.infer<typeof CreatorSocialSchema>;
export type CreatorProfileView = z.infer<typeof CreatorProfileViewSchema>;

/**
 * Тело PATCH /api/v2/buyer/creator/profile — все поля необязательные.
 *
 * Ручной интерфейс, а не `z.infer`: это ЗАПРОС, а не ответ. Zod здесь ничего не
 * проверяет — состав категорий/форматов/возрастов решает бэкенд
 * (`GET /creator/options` — SSOT), и вторая копия enum'ов на фронте нарушила бы
 * ADR-023 ровно так же, как копия списка площадок (см. комментарий выше).
 */
export interface CreatorProfileUpdate {
  categories?: string[];
  formats?: string[];
  gender?: string;
  age_range?: string;
  cities?: string[];
}

/** GET /api/v2/buyer/creator/profile — контентный профиль + все площадки (FTR-1458). */
export async function getCreatorProfile(): Promise<CreatorProfileView> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/profile');
  return CreatorProfileViewSchema.parse(raw);
}

/**
 * PATCH /api/v2/buyer/creator/profile — правка контентной части профиля (FTR-1458).
 *
 * Присылаем ТОЛЬКО изменённые поля: сервер правит присланное и не трогает
 * остальное. Пустой список — это значение (стереть список), `undefined` —
 * «не менять». Ответ совпадает по форме с GET, поэтому экран после сохранения
 * не ходит вторым запросом за площадками.
 *
 * Короткие имена (`categories`, `formats`, ...) — тот же контракт, что у мастера
 * онбординга; бэкенд принимает и длинные (BUG-1141 / ADR-022, alias symmetry с
 * `src/api/v2/buyer/_creator_schemas.py:CreatorProfileUpdateRequest`).
 */
export async function updateCreatorProfile(
  patch: CreatorProfileUpdate,
): Promise<CreatorProfileView> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/profile', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return CreatorProfileViewSchema.parse(raw);
}

/** POST /api/v2/buyer/creator/socials — добавить (или переписать) площадку. */
export async function addCreatorSocial(
  network: SocialNetwork,
  profileUrl: string,
): Promise<CreatorSocial> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/socials', {
    method: 'POST',
    body: JSON.stringify({ network, profile_url: profileUrl }),
  });
  return CreatorSocialSchema.parse(raw);
}

/** DELETE /api/v2/buyer/creator/socials/{network} — убрать площадку. */
export async function removeCreatorSocial(network: SocialNetwork): Promise<void> {
  await apiFetch(`/api/v2/buyer/creator/socials/${network}`, { method: 'DELETE' });
}
